import { useEffect, useState, useCallback } from "react";
import { Download, FileText, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, clean, download, errMsg } from "@/lib/api";

export function useFetch(path, params, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const key = JSON.stringify(params || {});
  const load = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(path, { params: clean(params) });
      setData(r.data);
    } catch (e) {
      setError(errMsg(e));
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, ...deps]);
  useEffect(() => {
    load();
  }, [load]);
  return { data, loading, error, reload: load, setData };
}

export function PageHeader({ overline, title, children, testid }) {
  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8 rise" data-testid={testid}>
      <div>
        {overline && <div className="text-[11px] uppercase font-bold tracking-[0.2em] text-[#F49619] mb-2">{overline}</div>}
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0E2D43]">{title}</h1>
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function Kpi({ label, value, sub, testid, tone = "accent", icon: Icon, delay = 0 }) {
  const bar = { accent: "border-l-[#F49619]", navy: "border-l-[#0E2D43]", danger: "border-l-rose-500", ok: "border-l-emerald-500" }[tone];
  return (
    <div
      className={`rise min-w-0 bg-white border border-slate-200 border-l-4 ${bar} rounded-xl p-4 sm:p-5 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md`}
      style={{ animationDelay: `${delay * 60}ms` }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase font-bold tracking-wider text-slate-500 leading-tight">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-slate-400 shrink-0" />}
      </div>
      <div className="mt-2 font-display text-2xl sm:text-[28px] font-bold text-[#0E2D43] tabular-nums" data-testid={`${testid}-value`}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export function Panel({ title, action, children, className = "", testid, flush }) {
  return (
    <section className={`min-w-0 bg-white border border-slate-200 rounded-xl rise ${className}`} data-testid={testid}>
      {title && (
        <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3.5 border-b border-slate-100">
          <h3 className="font-semibold text-[#0E2D43] text-base">{title}</h3>
          {action}
        </div>
      )}
      <div className={flush ? "" : "p-4 sm:p-5"}>{children}</div>
    </section>
  );
}

export const Plate = ({ children }) => (
  <span className="font-mono font-bold text-[13px] tracking-wider text-[#0E2D43] bg-white px-2 py-0.5 rounded border border-slate-300 shadow-[inset_4px_0_0_#1d4ed8]">
    {children}
  </span>
);

const LEVEL = {
  ok: ["OK", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  surveiller: ["À surveiller", "bg-amber-50 text-amber-700 border-amber-200"],
  anomalie: ["Anomalie", "bg-rose-50 text-rose-700 border-rose-200"],
};
export const LevelBadge = ({ v }) => <Chip cls={LEVEL[v]?.[1]}>{LEVEL[v]?.[0] || v}</Chip>;

export const AN_STATUS = {
  nouvelle: ["Nouvelle", "bg-blue-100 text-blue-800 border-blue-300"],
  a_traiter: ["À traiter", "bg-amber-100 text-amber-800 border-amber-300"],
  intervention_programmee: ["Intervention programmée", "bg-violet-100 text-violet-800 border-violet-300"],
  resolue: ["Résolue", "bg-emerald-100 text-emerald-800 border-emerald-300"],
};
export const AnomalyBadge = ({ v }) => <Chip cls={AN_STATUS[v]?.[1]}>{AN_STATUS[v]?.[0] || v}</Chip>;

export const V_STATUS = {
  actif: ["Actif", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  immobilise: ["Immobilisé", "bg-rose-50 text-rose-700 border-rose-200"],
  reparation: ["Réparation", "bg-amber-50 text-amber-700 border-amber-200"],
  sorti: ["Sorti du parc", "bg-slate-100 text-slate-600 border-slate-300"],
  archive: ["Archivé", "bg-zinc-800 text-white border-zinc-800"],
};
export const VehicleStatus = ({ v }) => <Chip testid="vehicle-status-chip" cls={V_STATUS[v]?.[1]}>{V_STATUS[v]?.[0] || v}</Chip>;
export const D_STATUS = {
  actif: ["Actif", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  inactif: ["Inactif", "bg-amber-50 text-amber-700 border-amber-200"],
  archive: ["Archivé", "bg-zinc-800 text-white border-zinc-800"],
};
export const DriverStatus = ({ v }) => <Chip testid="driver-status-chip" cls={D_STATUS[v]?.[1]}>{D_STATUS[v]?.[0] || v}</Chip>;

export const Chip = ({ cls = "bg-slate-100 text-slate-700 border-slate-200", children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold whitespace-nowrap ${cls}`}>
    {children}
  </span>
);

export const DemoTag = ({ show }) => (show ? <Chip cls="bg-[#FEF3E2] text-[#9a5a05] border-[#F49619]/40">DÉMO</Chip> : null);

export function ExportBtn({ path, params, filename, label = "Excel", pdf, testid }) {
  const [busy, setBusy] = useState(false);
  const Icon = pdf ? FileText : Download;
  const go = async () => {
    setBusy(true);
    try {
      await download(path, params, filename);
    } catch (e) {
      toast.error("Export impossible");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={go}
      disabled={busy}
      data-testid={testid}
      className="inline-flex items-center gap-2 h-10 px-3.5 rounded-md border border-slate-300 bg-white text-sm font-semibold text-[#0E2D43] hover:bg-slate-50 hover:border-[#0E2D43] transition-colors disabled:opacity-60"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      {label}
    </button>
  );
}

export const Loading = () => (
  <div className="flex items-center justify-center py-20 text-slate-400" data-testid="loading">
    <Loader2 className="w-6 h-6 animate-spin" />
  </div>
);

export const Empty = ({ text = "Aucune donnée" }) => (
  <div className="flex flex-col items-center justify-center py-10 text-slate-400 gap-2" data-testid="empty-state">
    <Inbox className="w-6 h-6" />
    <span className="text-sm">{text}</span>
  </div>
);

export function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5 min-w-0">
      <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">{label}</span>
      {children}
    </label>
  );
}

export function NSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <select className="field-select" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export const FilterBar = ({ children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 rise" data-testid={testid}>
    {children}
  </div>
);
